import {Component, Input, OnDestroy, OnInit, ViewChild, ViewContainerRef} from '@angular/core';
import {Connection, ConnectionType, Location, Meter, MeterType} from "@flowmaps-com/flowmaps-typescriptmodels";
import {RefdataUtils} from "../../refdata/refdata-utils";
import {AppContext} from "../../../app-context";
import {Entity} from "../../../handlers/entity";
import {Handler} from "../../../common/handler";
import {View} from "../../../common/view";
import {MeterComponentData, MeterDetailsComponent} from "../../refdata/meters/meter-details/meter-details.component";
import {TranslateDirective} from "../../../common/utils/translate.directive";
import {
    ConnectionComponentData,
    ConnectionDetailsComponent
} from "../../refdata/connections/connection-details/connection-details.component";
import {
    calculatedResidualEntity,
    effectiveMeterType,
    isCalculatedResidual,
    isChargePointMeter,
    isMeterVisible,
    isPercentageAllocated
} from '../../refdata/meters/submeter-role.utils';
import {openModal as openGlobalModal} from '../../../common/app-common-utils';
import {ChartModalOptions} from '../../charts/chart-utils.service';
import {MeasurementsHandlerOpenModalCommand} from '../../measurements-component/measurements-handler.component';
import {MeterTreeName} from '../../dashboard/dashboard.types';
import {meterTreeName} from '../../charts/intermediate-meter.utils';
import {MeterTreeNamePreferenceService} from '../../charts/meter-tree-name-preference.service';
import {Subscription} from 'rxjs';

@Component({
    selector: 'app-location-meters',
    templateUrl: './location-meters.component.html',
    styleUrls: ['./location-meters.component.scss']
})
@Handler()
export class LocationMetersComponent extends View implements OnInit, OnDestroy {
    appContext = AppContext;
    refDataUtils = RefdataUtils;
    @Input() location: Entity;
    @ViewChild('container', {read: ViewContainerRef}) container: ViewContainerRef;
    modalOptions: ChartModalOptions = {
        fullScreen: false
    };
    meters: Entity[];
    expandedChargePointConnections = new Set<string>();
    expandedSplitMeters = new Set<string>();
    showHiddenMetersForConnections = new Set<string>();
    selectedMeterName: MeterTreeName = MeterTreeName.Name;
    private allocatedChildrenByConnection: AllocatedChildrenByConnection = new Map();
    private meterNameSubscription: Subscription;

    constructor(private readonly meterTreeNamePreference: MeterTreeNamePreferenceService) {
        super();
    }

    @Input()
    set data(data: ChartModalOptions) {
        this.modalOptions = data || {fullScreen: false};
    }

    ngOnInit() {
        this.selectedMeterName = this.meterTreeNamePreference.selectedName(AppContext.userProfile?.userId);
        this.meterNameSubscription = this.meterTreeNamePreference.selectedName$
            .subscribe(selectedName => this.selectedMeterName = selectedName);
        this.subscribeTo("getEntity", this.location.location.locationId).subscribe((location: Entity) => {
            if (!location?.location) return;
            this.location = location;
            this.meters = location.location.connections.flatMap(connection =>
                connection.meters.map(meter => new Entity(
                    location.organisation, location.location, connection, meter)));
            this.updateAllocatedChildrenIndex();
        });
    }

    ngOnDestroy() {
        this.meterNameSubscription?.unsubscribe();
    }

    getMetersForConnectionType = (connectionType: ConnectionType | string, primaryOnly: boolean): Entity[] => {
        return (this.meters || [])
            .filter(c => c.connection.info.connectionType === connectionType)
            .filter(c => {
                const type = effectiveMeterType(c.meter);
                return primaryOnly ? type === MeterType.PRIMARY : type !== MeterType.PRIMARY;
            })
            .filter(meter => !this.isDuplicateInnaxFeedInMeter(meter));
    }

    getActiveMetersForConnectionType = (connectionType: ConnectionType | string, primaryOnly: boolean): Entity[] =>
        this.getMetersForConnectionType(connectionType, primaryOnly).filter(this.isActiveMeter);

    getSubMetersForMeter = (primaryMeter: Entity): Entity[] => {
        const meters = (this.meters || [])
        .filter(meter => meter.connection.connectionId === primaryMeter.connection.connectionId)
        .filter(meter => effectiveMeterType(meter.meter) === MeterType.INTERMEDIATE)
        .filter(this.isActiveMeter);
        if (!meters.some(entity => isCalculatedResidual(entity.meter))) {
            const residual = calculatedResidualEntity(primaryMeter.organisation, primaryMeter.location,
                primaryMeter.connection);
            if (residual) meters.push(residual);
        }
        return meters;
    };

    getVisibleSubMetersForMeter = (primaryMeter: Entity): Entity[] =>
        this.withHiddenMetersFilter(primaryMeter.connection.connectionId, this.getSubMetersForMeter(primaryMeter));

    getVisibleRootSubMetersForMeter = (primaryMeter: Entity): Entity[] =>
        withoutNestedAllocatedChildren(this.withHiddenMetersFilter(
            primaryMeter.connection.connectionId, this.getSubMetersForMeter(primaryMeter)));

    getVisibleAllocatedChildrenForMeter = (sourceMeter: Entity): Entity[] =>
        this.withHiddenMetersFilter(sourceMeter.connection.connectionId,
            this.allocatedChildrenByConnection.get(sourceMeter.connection.connectionId)
                ?.get(sourceMeter.meter.meterId) || []);

    isSplitMeterExpanded = (sourceMeter: Entity): boolean =>
        this.expandedSplitMeters.has(this.splitMeterKey(
            sourceMeter.connection.connectionId, sourceMeter.meter.meterId));

    toggleSplitMeter = (sourceMeter: Entity): void => {
        const key = this.splitMeterKey(sourceMeter.connection.connectionId, sourceMeter.meter.meterId);
        if (this.expandedSplitMeters.has(key)) {
            this.expandedSplitMeters.delete(key);
        } else {
            this.expandedSplitMeters.add(key);
        }
    };

    getGrossProductionMetersForMeter = (primaryMeter: Entity): Entity[] => (this.meters || [])
        .filter(meter => meter.connection.connectionId === primaryMeter.connection.connectionId)
        .filter(meter => effectiveMeterType(meter.meter) === MeterType.GROSS_PRODUCTION)
        .filter(this.isActiveMeter);

    getVisibleGrossProductionMetersForMeter = (primaryMeter: Entity): Entity[] =>
        this.withHiddenMetersFilter(primaryMeter.connection.connectionId, this.getGrossProductionMetersForMeter(primaryMeter));

    getStandaloneMetersForConnectionType = (connectionType: ConnectionType | string): Entity[] => (this.meters || [])
        .filter(meter => meter.connection.info.connectionType === connectionType)
        .filter(meter => {
            const type = effectiveMeterType(meter.meter);
            return type !== MeterType.PRIMARY && type !== MeterType.INTERMEDIATE;
        });

    get electricityMeterCount(): number {
        return (this.meters || [])
            .filter(meter => meter.connection.info.connectionType === ConnectionType.Electricity)
            .filter(meter => !this.isDuplicateInnaxFeedInMeter(meter))
            .filter(this.isActiveMeter).length;
    }

    get chargePointMeterCount(): number {
        return (this.meters || [])
            .filter(meter => meter.connection.info.connectionType === ConnectionType.ChargePoint)
            .filter(this.isActiveMeter).length;
    }

    get chargePointMeterGroups(): ChargePointMeterGroup[] {
        const byConnection = new Map<string, Entity[]>();
        (this.meters || []).filter(meter => meter.connection.info.connectionType === ConnectionType.ChargePoint)
            .filter(this.isActiveMeter)
            .forEach(meter => {
                const connectionId = meter.connection.connectionId;
                byConnection.set(connectionId, [...(byConnection.get(connectionId) || []), meter]);
            });
        return Array.from(byConnection.entries()).map(([connectionId, meters]) => {
            const aggregate = meters.find(meter => meter.meter.info.virtual)
                || meters.find(meter => effectiveMeterType(meter.meter) === MeterType.PRIMARY);
            return {
                connectionId,
                aggregate,
                channels: this.withHiddenMetersFilter(connectionId, meters.filter(meter => meter !== aggregate)),
                hasHiddenChannels: meters.filter(meter => meter !== aggregate).some(this.isHiddenMeter),
                label: aggregate ? RefdataUtils.meterFormatter(aggregate)
                    : RefdataUtils.connectionFormatter(meters[0].connection)
            };
        });
    }

    isChargePointGroupExpanded = (connectionId: string): boolean =>
        this.expandedChargePointConnections.has(connectionId);

    toggleChargePointGroup = (connectionId: string): void => {
        if (this.expandedChargePointConnections.has(connectionId)) {
            this.expandedChargePointConnections.delete(connectionId);
        } else {
            this.expandedChargePointConnections.add(connectionId);
        }
    };

    hasHiddenMetersForConnection = (connectionId: string): boolean =>
        (this.meters || []).some(meter => meter.connection.connectionId === connectionId
            && this.isActiveMeter(meter)
            && this.isHiddenMeter(meter));

    showHiddenMetersForConnection = (connectionId: string): boolean =>
        this.showHiddenMetersForConnections.has(connectionId);

    toggleHiddenMetersForConnection(event: Event, connectionId: string): void {
        event.preventDefault();
        event.stopPropagation();
        if (this.showHiddenMetersForConnections.has(connectionId)) {
            this.showHiddenMetersForConnections.delete(connectionId);
        } else {
            this.showHiddenMetersForConnections.add(connectionId);
        }
    }

    editMeter = (meter: Entity) => openGlobalModal(MeterDetailsComponent, <MeterComponentData>{
        entity: meter
    }, undefined, {
        cssClass: 'modal-xl meter-details-modal',
        scrollable: false,
        centered: true
    });

    editConnection = (meter: Entity) => super.openModal(ConnectionDetailsComponent, <ConnectionComponentData>{
        organisation: meter.organisation,
        location: meter.location,
        connection: meter.connection
    }, {
        cssClass: 'modal-xl connection-details-modal',
        scrollable: false,
        centered: true
    });

    openModal() {
        this.sendCommandAndForget("openChartInModal", <MeasurementsHandlerOpenModalCommand>{
            component: LocationMetersComponent,
            chartModalData: this.modalOptions,
            inputs: {
                location: this.location
            },
            container: this.container,
            modalHeightScale: 3
        });
    }

    closeModal = () => this.sendCommandAndForget("closeModal");

    accordionId = (): string => this.modalOptions.fullScreen
        ? 'locationMeterAccordionFullscreen'
        : 'locationMeterAccordion';

    collapseId = (id: string): string => this.modalOptions.fullScreen ? `${id}Fullscreen` : id;

    isEditableMeter = (meter: Entity): boolean =>
        effectiveMeterType(meter.meter) !== MeterType.PRIMARY;

    meterConnectionType = (meter: Entity): ConnectionType | string =>
        isChargePointMeter(meter) ? ConnectionType.ChargePoint : meter.connection.info.connectionType;

    meterType = (meter: Entity): MeterType => effectiveMeterType(meter.meter) as MeterType;

    isResidual = (meter: Entity): boolean => isCalculatedResidual(meter.meter);

    private isActiveMeter = (meter: Entity): boolean => meter.meter.active !== false;

    private isHiddenMeter = (meter: Entity): boolean => !isMeterVisible(meter.meter);

    private withHiddenMetersFilter(connectionId: string, meters: Entity[]): Entity[] {
        return this.showHiddenMetersForConnections.has(connectionId)
            ? meters
            : meters.filter(meter => !this.isHiddenMeter(meter));
    }

    private updateAllocatedChildrenIndex(): void {
        this.allocatedChildrenByConnection = indexAllocatedChildren(this.meters);
        const allocatedParentKeys = new Set<string>();
        this.allocatedChildrenByConnection.forEach((childrenByParent, connectionId) =>
            childrenByParent.forEach((children, parentId) => {
                if (children.length) allocatedParentKeys.add(this.splitMeterKey(connectionId, parentId));
            }));
        this.expandedSplitMeters.forEach(key => {
            if (!allocatedParentKeys.has(key)) this.expandedSplitMeters.delete(key);
        });
    }

    private splitMeterKey(connectionId: string, meterId: string): string {
        return `${connectionId}:${meterId}`;
    }

    private isDuplicateInnaxFeedInMeter(meter: Entity): boolean {
        const connectionMeters = meter.connection?.meters || [];
        const hasPrimaryInnaxMeter = (label: 'ODN' | 'LDN') => connectionMeters.some(candidate =>
            candidate.active !== false
            && this.isInnaxElectricityPrimary(candidate, meter.connection)
            && this.hasMeterLabel(candidate, label));

        return this.isInnaxElectricityPrimary(meter.meter, meter.connection)
            && this.hasMeterLabel(meter.meter, 'ODN')
            && hasPrimaryInnaxMeter('ODN')
            && hasPrimaryInnaxMeter('LDN');
    }

    private isInnaxElectricityPrimary(meter: Meter, connection: Connection): boolean {
        const provider = meter?.info?.surveyor?.name || connection?.info?.surveyor?.name;
        return connection?.info?.connectionType === ConnectionType.Electricity
            && effectiveMeterType(meter) === MeterType.PRIMARY
            && (provider?.toLowerCase() === 'innax' || meter?.meterId?.startsWith('innax-'));
    }

    private hasMeterLabel(meter: Meter, label: 'ODN' | 'LDN'): boolean {
        return meter?.info?.label?.includes(`Hoofdmeting elektriciteit ${label}`) === true;
    }

    meterDisplayLabel = (meter: Entity): string => {
        const label = meterTreeName(meter, this.selectedMeterName);
        return label?.trim().toLowerCase() === 'measured gross pv production'
            ? TranslateDirective.getTranslation('measured gross pv production', true) as string
            : label;
    };

    primaryMeterDisplayLabel = (meter: Entity): string =>
        this.customConnectionLabel(meter) || this.sourceMeterDisplayLabel(meter);

    chargePointAggregateDisplayLabel = (meter?: Entity): string => meter
        ? this.customConnectionLabel(meter) || `${meter.connection.info.code}${this.providerSuffix(meter)}`
        : '';

    grossProductionDisplayLabel = (meter: Entity): string =>
        meter.meter.details?.label?.trim() || this.sourceMeterDisplayLabel(meter);

    private sourceMeterDisplayLabel(meter: Entity): string {
        const sourceMeterNumber = (meter.meter.info.meterNumbers || []).filter(Boolean).join(', ')
            || meter.meter.info.accessPointId;
        return `${meter.connection.info.code} - ${sourceMeterNumber}${this.providerSuffix(meter)}`;
    }

    private providerSuffix(meter: Entity): string {
        const provider = meter.meter.info.surveyor?.name || meter.connection.info.surveyor?.name;
        return provider ? ` (${provider})` : '';
    }

    private customConnectionLabel(meter: Entity): string {
        const label = meter.connection.info.label?.trim();
        return label || null;
    }

    trackBy: any = (index: number, item: MeterWithConnection) => {
        return item?.meter?.meterId;
    };

    trackByChargePointGroup = (index: number, group: ChargePointMeterGroup): string => group.connectionId;
}

export interface MeterWithConnection {
    meter: Meter;
    connection: Connection;
    location: Location;
}

export interface ChargePointMeterGroup {
    connectionId: string;
    aggregate?: Entity;
    channels: Entity[];
    hasHiddenChannels: boolean;
    label: string;
}

export type AllocatedChildrenByConnection = Map<string, Map<string, Entity[]>>;

export function withoutNestedAllocatedChildren(meters: Entity[] = []): Entity[] {
    const visibleMeterIdsByConnection = new Map<string, Set<string>>();
    meters.forEach(entity => {
        const connectionId = entity.connection.connectionId;
        const meterIds = visibleMeterIdsByConnection.get(connectionId) || new Set<string>();
        meterIds.add(entity.meter.meterId);
        visibleMeterIdsByConnection.set(connectionId, meterIds);
    });
    return meters.filter(entity => !isPercentageAllocated(entity.meter)
        || !visibleMeterIdsByConnection.get(entity.connection.connectionId)?.has(entity.meter.parentId));
}

export function indexAllocatedChildren(meters: Entity[] = []): AllocatedChildrenByConnection {
    const result: AllocatedChildrenByConnection = new Map();
    meters.filter(entity => entity.meter?.active !== false
        && isPercentageAllocated(entity.meter)
        && !!entity.meter.parentId)
        .forEach(entity => {
            const connectionId = entity.connection.connectionId;
            const parentId = entity.meter.parentId;
            const childrenByParent = result.get(connectionId) || new Map<string, Entity[]>();
            childrenByParent.set(parentId, [...(childrenByParent.get(parentId) || []), entity]);
            result.set(connectionId, childrenByParent);
        });
    return result;
}
